import { useEffect, useState } from 'react';

import { ANCHOR_TIMEOUT_MS } from './types.js';

export type Rect = { top: number; left: number; width: number; height: number };

export type AnchorResult =
  /** Still looking, or looking again. The step is not shown. */
  | { status: 'waiting' }
  /** Found and measured. Re-measured as the page scrolls or reflows. */
  | { status: 'found'; rect: Rect }
  /** Not there within the timeout. The caller skips the step. */
  | { status: 'missing' };

/** One anchor name, or several in order of preference. */
export type AnchorNames = string | readonly string[];

const same = (a: Rect, b: Rect) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/**
 * Find `[data-coach="<anchor>"]`, wait for it if it isn't there yet, and keep
 * its position current.
 *
 * The waiting is the point. A coached flow on a live UI constantly points at
 * things that appear in response to the previous step — tap "Add habit" and
 * the next anchor is a field inside a composer that did not exist a moment
 * ago. Treating "not found" as failure would break every second step.
 *
 * Giving up is also the point. Flows outlive the screens they describe, so a
 * missing anchor has to end in a skipped step rather than a spinner over a
 * frozen overlay, which is the worst outcome available.
 *
 * SEVERAL NAMES may be given, and the first one on screen wins. This is what
 * lets one step follow a control that is REPLACED by what it opens: "set your
 * goal" points at the button, and the moment the button becomes the composer,
 * the same step points at the composer. Without it the spotlight froze on the
 * coordinates of an element that no longer existed and rang a rectangle in
 * the middle of the new screen.
 *
 * Resolution runs every frame rather than off a MutationObserver, because
 * "the element I am pointing at has been unmounted" is not a mutation the
 * observer's subtree filter reliably reports, and position already needed a
 * frame loop: it can change from scrolling, layout, animation or a virtual
 * keyboard, and only one of those fires an event we could listen to.
 *
 * The result is stored WITH the anchor it describes, and reported as
 * `waiting` until those agree. Without that pairing the hook returns the
 * previous anchor's verdict for one render after the anchor changes, and a
 * caller acting on it skips a step it never actually looked for — which,
 * because skips are counted, silently retires the whole flow.
 */
export function useAnchor(anchor: AnchorNames | null, waitMs = ANCHOR_TIMEOUT_MS): AnchorResult {
  // A stable identity for the list, so passing a fresh array every render
  // does not restart the search every render. Always an array, and always
  // JSON: it is unambiguous for any anchor name, needs no delimiter, and
  // reads back as exactly the list that went in.
  const key =
    anchor === null ? null : JSON.stringify(typeof anchor === 'string' ? [anchor] : anchor);

  const [state, setState] = useState<{ key: string | null; result: AnchorResult }>({
    key: null,
    result: { status: 'waiting' },
  });

  useEffect(() => {
    if (key === null) return;
    const names = JSON.parse(key) as string[];

    let frame = 0;
    let deadline = Date.now() + waitMs;
    let scrolled: Element | null = null;
    let last: Rect | null = null;

    const resolve = (): { element: Element; rect: Rect } | null => {
      for (const name of names) {
        const element = document.querySelector(`[data-coach="${CSS.escape(name)}"]`);
        if (!element) continue;
        const box = element.getBoundingClientRect();
        // A found-but-collapsed element counts as not found: something is
        // mid-transition, and spotlighting a zero-sized box draws a hole in
        // the corner of the screen.
        if (box.width === 0 && box.height === 0) continue;
        return {
          element,
          rect: { top: box.top, left: box.left, width: box.width, height: box.height },
        };
      }
      return null;
    };

    const tick = () => {
      const hit = resolve();

      if (hit) {
        // Bring it into view ONCE per element. A spotlight on something below
        // the fold dims the screen and rings nothing the student can see,
        // which is the worst state this component has. Once only, because
        // repeating it every frame would fight anyone scrolling away
        // deliberately. Guarded: jsdom does not implement it, and a tutorial
        // must never be the thing that throws.
        if (hit.element !== scrolled) {
          scrolled = hit.element;
          hit.element.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        }
        // The clock restarts while the target is present, so an anchor that
        // disappears late gets the same grace as one that never arrived.
        deadline = Date.now() + waitMs;
        if (last === null || !same(last, hit.rect)) {
          last = hit.rect;
          setState({ key, result: { status: 'found', rect: hit.rect } });
        }
      } else if (Date.now() > deadline) {
        setState({ key, result: { status: 'missing' } });
        return; // Stop the loop; the caller retires the step.
      } else if (last !== null) {
        // Gone, but not yet given up on. Hide rather than hold the last
        // position: a ring around where a button used to be is worse than no
        // ring at all, because it points at whatever replaced it.
        last = null;
        scrolled = null;
        setState({ key, result: { status: 'waiting' } });
      }

      frame = requestAnimationFrame(tick);
    };

    tick();
    return () => cancelAnimationFrame(frame);
  }, [key, waitMs]);

  return state.key === key ? state.result : { status: 'waiting' };
}
